/**
 * What a trip IS: its canonical shape, and the one place the two copies of a
 * trip's identity are kept in step. Pure — no Firebase, no DOM, no clock of
 * its own.
 *
 * ── The asymmetry this file exists to end ─────────────────────────────────
 *
 * A trip stores some values TWICE: once at the top level (`trip.clientName`,
 * `trip.checkin`) where the dashboard reads them, and once inside `trip.data`
 * (`data.clientName`, `data.checkin`) where the editor's page templates read
 * them. Legacy keeps them in step in one direction only:
 *
 *   `createTrip`   (app.html:6241, and the live override at 8399) writes
 *                  `trip.clientName` / `trip.checkin` / `trip.checkout` and
 *                  leaves `data` as `{}`.
 *   `saveTrip`     (app.html:6624, live override at 8436) copies
 *                  `data.clientName` -> `trip.clientName` and
 *                  `data.checkin`    -> `trip.checkin`. Never the reverse.
 *
 * So between "+ New Trip" and the first time the advisor happens to press
 * Save on the Trip page, `data` is empty and every reader that looks there
 * sees nothing. Three shipped bugs come straight out of that one gap:
 *
 *   1. The Upcoming Reminders strip (app.html:8709 reads `t.data.checkin`) is
 *      EMPTY for every trip made with + New Trip, however imminent, while the
 *      trip card three inches away counts down "3 days away" from
 *      `trip.checkin`. — HANDOFF §5t #2.
 *   2. Pending Commission (app.html:5999 reads `t.data.checkin`) skips the
 *      same trips. — HANDOFF §5t #5.
 *   3. The Zapier `trip_created` webhook fires with
 *      `client_first_name: ''` and `client_last_name: ''`, because
 *      `buildSheetRow` (app.html:8272, fields at 8280-8281) reads
 *      `d.clientFirstName` / `d.clientLastName` with no fallback to
 *      `trip.clientName`. Every trip ever created from the modal reached the
 *      advisor's Google Sheet with a blank name. — HANDOFF §5u #6.
 *
 * `mirrorTrip()` below is applied by BOTH create and save, so the two paths
 * can no longer disagree. It is the same rule legacy applies on save, made
 * symmetric: a non-empty `data` value wins (the editor wrote it most
 * recently); otherwise the top-level value is copied down.
 */

import { commSplitOf } from './split.js'

/** Bump when the persisted shape changes. ARCHITECTURE.md §7. */
export const TRIP_SCHEMA_VERSION = 1

/**
 * Every trip status the product recognises — what may be STORED.
 *
 * `booked` was added on 17 Aug 2026. It is the status legacy's menu has always
 * written (app.html:4031) and it was missing from this list, so `newTrip`
 * coerced it to `planning` and `trip-import.js` turned every "Booked" column
 * in a spreadsheet import into a planning trip. Trips already stored as
 * `booked` were unaffected: `normalizeTrip` keeps an unknown status, which is
 * why the two paths disagreed instead of the bug being obvious.
 *
 * `confirmed` stays although no control offers it. `money.js#isBooked` treats
 * it as `booked`, and a status that is stored somewhere must keep meaning what
 * it meant — never repurpose or drop a field (ARCHITECTURE §7).
 */
export const TRIP_STATUSES = Object.freeze([
  'planning', 'booked', 'confirmed', 'completed', 'cancelled',
])

/**
 * The statuses a control may OFFER. A shorter list than `TRIP_STATUSES`, on
 * purpose, and for two different reasons.
 *
 * `confirmed` is storable and is NOT offered. It means the same thing as
 * `booked` to `money.js#isBooked`, and two names for one state on the same
 * board is how a status stops meaning anything. Trips already carrying it keep
 * it; nothing writes it again.
 *
 * `cancelled` IS offered, on Trey's decision, 17 Aug 2026. It was already
 * load-bearing and unreachable: `isCancelled` reads it, and `summaryTotals`
 * and `revenue.js#revenueTotals` drop a cancelled trip from every figure they
 * produce. So the app has always known how not to count a cancelled trip, and
 * until now no advisor could say one was — they had to delete it, which loses
 * the itinerary, or leave it counting as live work.
 *
 * Everything else here is legacy's status menu, verbatim (app.html:4030-4032),
 * so a status set in /v2 means what one set in /app means.
 */
export const EDITABLE_STATUSES = Object.freeze([
  'planning', 'booked', 'completed', 'cancelled',
])

/**
 * May a control set this status? The guard for the one write whose value
 * comes straight off an element on screen.
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export function isEditableStatus(status) {
  return EDITABLE_STATUSES.includes(text(status).toLowerCase())
}

/**
 * Values stored both at the top level and in `data`, under the same name.
 * Adding a name here is all it takes to keep a new pair honest.
 */
export const MIRRORED_FIELDS = Object.freeze(['clientName', 'checkin', 'checkout'])

/**
 * The per-page client-name boxes legacy's `saveTrip` auto-fills from
 * `data.clientName` (app.html:6631). Kept verbatim: these are the
 * `data-field` names in `PAGE_TEMPLATES`, so a typo here is a blank heading
 * on a printed itinerary.
 */
export const CLIENT_NAME_FIELDS = Object.freeze([
  'tripClient', 'coverClient', 'accomClient', 'actClient', 'docClient',
  'genClient', 'packClient', 'ctClient', 'noteClient', 'crItClient',
])

/** @param {unknown} value @returns {string} */
function text(value) {
  return value == null ? '' : String(value).trim()
}

/** @param {any} trip @returns {Record<string, any>} */
function fields(trip) {
  return (trip && trip.data) || {}
}

/**
 * Split a display name into first and last, for the fields that want them
 * separately. A guess, and named like one: everything after the first space
 * is the surname, which is right for "Marissa Thomson" and wrong for
 * "Ana Maria de la Cruz". It only ever fills a field that is EMPTY, so an
 * advisor who typed the parts themselves is never overruled.
 *
 * @param {unknown} name
 * @returns {{first: string, last: string}}
 */
export function splitPersonName(name) {
  const whole = text(name).replace(/\s+/g, ' ')
  if (!whole) return { first: '', last: '' }
  const space = whole.indexOf(' ')
  if (space === -1) return { first: whole, last: '' }
  return { first: whole.slice(0, space), last: whole.slice(space + 1) }
}

/** The client's name, wherever it is stored. @param {any} trip @returns {string} */
export function tripClientName(trip) {
  const d = fields(trip)
  const parts = `${text(d.fname)} ${text(d.lname)}`.trim()
  return text(trip && trip.clientName) || text(d.clientName) || text(d.coverClient) || parts
}

/** @param {any} trip @returns {string} */
export function tripDestination(trip) {
  const d = fields(trip)
  return text(trip && trip.destination) || text(d.coverDest) || text(d.destination)
}

/**
 * One field, whether this is a whole trip or a summary.
 *
 * A trip keeps most of these under `data`; `trip-summary.js` carries them
 * flattened so a screen can be drawn from the one-document index instead of
 * one read per trip. The accessors around this one already prefer the flat
 * value — the dashboard's payment dates, supplier and paid-month did not, and
 * reading only `data` gave a summarised trip empty values for all three.
 * Every one of those is a legitimate empty, so nothing would have thrown.
 *
 * @param {any} trip @param {string} name @returns {any}
 */
export function tripField(trip, name) {
  if (!trip) return ''
  const flat = trip[name]
  if (flat !== undefined && flat !== null && flat !== '') return flat
  const d = (trip && trip.data) || {}
  return d[name]
}

/**
 * The supplier or resort, for search and for the sheet row.
 *
 * Flat field first, like every accessor beside it. A trip stores this under
 * `data`, but a SUMMARY (`trip-summary.js`) carries it flattened, and a screen
 * reading the summary index instead of whole trips must get the same answer.
 * Reading only `data` returned an empty supplier for every summarised trip —
 * silently, since an empty string is a legitimate value here.
 *
 * @param {any} trip @returns {string}
 */
export function tripSupplier(trip) {
  return text(trip && trip.supplier) || text(fields(trip).supplier)
}

/** Departure date as the advisor typed it. @param {any} trip @returns {string} */
export function tripCheckin(trip) {
  return text(trip && trip.checkin) || text(fields(trip).checkin)
}

/** Return date as the advisor typed it. @param {any} trip @returns {string} */
export function tripCheckout(trip) {
  return text(trip && trip.checkout) || text(fields(trip).checkout)
}

/**
 * Is this trip archived — hidden from the active list?
 *
 * Ported from app.html:5814. Two things archive a trip and legacy taught only
 * some of its readers about both: `status === 'completed'` and the AI
 * assistant's archive action, which sets `archived = true` and leaves the
 * status alone (app.html:14949).
 *
 * @param {any} trip
 * @returns {boolean}
 */
export function isArchivedTrip(trip) {
  if (!trip) return false
  if (trip.archived === true) return true
  const status = String(trip.status || '').toLowerCase()
  return status === 'completed' || status === 'archived'
}

/**
 * The "Mar 14 - Mar 21" label, built the way legacy builds it (app.html:6246).
 * @param {unknown} checkin @param {unknown} checkout @returns {string}
 */
export function tripDates(checkin, checkout) {
  const from = text(checkin)
  const to = text(checkout)
  if (from && to) return `${from} - ${to}`
  return from || to || ''
}

/**
 * Put the two copies of a trip's identity back in step, in both directions.
 *
 * Rules, in order:
 *   1. A non-empty `data.<field>` wins — the editor wrote it most recently.
 *      This is exactly what legacy `saveTrip` does, so nothing regresses.
 *   2. Otherwise a non-empty `trip.<field>` is copied INTO `data`. This is
 *      the half legacy never had.
 *   3. `dates` is rebuilt from the agreed check-in/check-out, but only when
 *      one of them exists — a trip whose dates were typed as free text keeps
 *      what the advisor typed.
 *   4. The per-page client boxes and `clientFirstName` / `clientLastName` are
 *      filled from the agreed client name, but only where empty.
 *
 * Returns a NEW trip; the input is not mutated. Idempotent: mirroring a
 * mirrored trip changes nothing, which is what makes it safe to run on every
 * read as well as every write.
 *
 * @param {any} trip
 * @returns {any}
 */
export function mirrorTrip(trip) {
  const source = trip || {}
  const out = { ...source }
  const data = { ...fields(source) }

  for (const key of MIRRORED_FIELDS) {
    const agreed = text(data[key]) || text(source[key])
    if (!agreed) continue
    out[key] = agreed
    data[key] = agreed
  }

  if (out.checkin || out.checkout) out.dates = tripDates(out.checkin, out.checkout)
  else out.dates = text(source.dates)

  const name = text(out.clientName) || tripClientName(source)
  if (name) {
    out.clientName = name
    data.clientName = name
    for (const key of CLIENT_NAME_FIELDS) if (!text(data[key])) data[key] = name
    const { first, last } = splitPersonName(name)
    if (!text(data.clientFirstName) && first) data.clientFirstName = first
    if (!text(data.clientLastName) && last) data.clientLastName = last
  }

  out.data = data
  return out
}

/**
 * A brand-new trip, in the shape Firestore stores.
 *
 * Everything the caller does not supply has a defined default, so no screen
 * has to remember which fields legacy happened to set. `id` and `created`
 * are injected rather than read from `Date.now()` here, because a pure
 * function that reaches for a clock cannot be tested and because bulk import
 * needs ids that do not collide inside one millisecond.
 *
 * @param {{
 *   id: string, created?: number, clientName?: string, destination?: string,
 *   checkin?: string, checkout?: string, dates?: string, preset?: string,
 *   pages?: string[], status?: string, clientId?: string, imported?: boolean,
 *   data?: Record<string, any>, commSplit?: unknown
 * }} input
 * @returns {any}
 */
export function newTrip(input) {
  const source = input || /** @type {any} */ ({})
  if (!text(source.id)) throw new Error('newTrip requires an id')

  const status = TRIP_STATUSES.includes(String(source.status)) ? String(source.status) : 'planning'
  const trip = {
    id: text(source.id),
    schemaVersion: TRIP_SCHEMA_VERSION,
    clientName: text(source.clientName),
    destination: text(source.destination),
    dates: text(source.dates),
    preset: text(source.preset) || 'custom',
    status,
    pages: Array.isArray(source.pages) ? source.pages.slice() : [],
    data: { ...(source.data || {}) },
    created: Number.isFinite(source.created) ? Number(source.created) : 0,
    checkin: text(source.checkin),
    checkout: text(source.checkout),
  }
  if (text(source.clientId)) trip.clientId = text(source.clientId)
  if (source.imported === true) trip.imported = true
  /* The commission split this trip is on, stamped at creation from the account
     default. Only set when there IS one: a trip WITHOUT the field follows the
     account default, so writing an empty one would make every trip immune to a
     later change. Trips created before 19 Aug 2026 have no field and keep
     following. See `lib/split.js#tripSplit`. */
  const split = commSplitOf(source.commSplit)
  if (split) trip.commSplit = split
  return mirrorTrip(trip)
}


/**
 * A trip as read from Firestore, made safe to hand to anything.
 *
 * The document id wins over any `id` inside the document. `/app` uses
 * `trip.id` as the document id (app.html:8194) so for every trip it has ever
 * written the two agree — but a document written any other way would produce
 * a card whose "open" link pointed at a trip that does not exist.
 *
 * @param {any} raw
 * @param {string} [docId]
 * @returns {any}
 */
export function normalizeTrip(raw, docId) {
  const source = raw || {}
  const trip = mirrorTrip({ ...source })
  const id = text(docId) || text(source.id)
  if (id) trip.id = id
  if (!Array.isArray(trip.pages)) trip.pages = []
  if (!TRIP_STATUSES.includes(String(trip.status))) trip.status = text(trip.status) || 'planning'
  return trip
}

/**
 * v0 -> v1. Idempotent, per ARCHITECTURE.md §7, so it is safe to run on every
 * load and safe to run twice from a migration script.
 *
 * v0 is "whatever `/app` wrote": no `schemaVersion`, and the mirrored fields
 * populated on one side only. v1 adds the version stamp and mirrors. Nothing
 * is renamed and nothing is dropped — never repurpose a field.
 *
 * @param {any} trip
 * @returns {{trip: any, changed: boolean}}
 */
export function migrateTrip(trip) {
  const before = JSON.stringify(trip || null)
  const out = normalizeTrip(trip)
  out.schemaVersion = TRIP_SCHEMA_VERSION
  return { trip: out, changed: JSON.stringify(out) !== before }
}

/**
 * Is this `finTotal` a single number, or is it a budget band?
 *
 * `finTotalField` is `readonly` in the markup (app.html:5159) and is computed
 * by `calcFinTotal` from the line items, so no advisor can type into it.
 * Exactly two writers bypass the calculator — the AI assistant's trip-update
 * action (app.html:14890) and the spreadsheet importer (app.html:15274,
 * hints at 15292, which maps any column named total / revenue / saleamount
 * into it). One production trip therefore holds `"$10,000 - $20,000"`, a
 * band straight off the intake form's budget dropdown, sitting in a revenue
 * field. That string is what produced `/insights`' $1,000,020,000.
 *
 * `money.js#amount` reads the FIRST number and stops, so it turns the band
 * into $10,000 — sane, and still not revenue. This says so out loud instead.
 *
 * @param {unknown} value
 * @returns {{ok: boolean, reason: 'empty'|'range'|'not-a-number'|'', value: string}}
 */
export function checkQuotedTotal(value) {
  const raw = text(value)
  if (!raw) return { ok: false, reason: 'empty', value: '' }
  /* Thousands separators are part of one number; anything else that produces
     a second number means the field is holding more than a total. */
  const cleaned = raw.replace(/(\d),(?=\d{3}\b)/g, '$1')
  const numbers = cleaned.match(/\d+(?:\.\d+)?/g) || []
  if (numbers.length === 0) return { ok: false, reason: 'not-a-number', value: '' }
  if (numbers.length > 1) return { ok: false, reason: 'range', value: '' }
  return { ok: true, reason: '', value: raw }
}
